import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, RefreshCcw } from "lucide-react";
import { useI18n } from "../../i18n";

const SMOOBU_APT_ID = "1742047";
const SCRIPT_SRC = "https://login.smoobu.com/js/Settings/BookingToolIframe.js";

/**
 * Load the Smoobu init script with retry. Returns a Promise<boolean>
 * resolving to true once window.BookingToolIframe is available.
 */
function loadSmoobuScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.BookingToolIframe) return Promise.resolve(true);
  if (window.__smoobuLoading) return window.__smoobuLoading;

  window.__smoobuLoading = new Promise((resolve) => {
    const tryLoad = (attempt) => {
      const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
      const onReady = () => {
        if (window.BookingToolIframe) resolve(true);
        else if (attempt < 3) setTimeout(() => tryLoad(attempt + 1), 1200 * attempt);
        else resolve(false);
      };

      if (existing) {
        if (window.BookingToolIframe) return resolve(true);
        existing.addEventListener("load", onReady, { once: true });
        return;
      }
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.crossOrigin = "anonymous";
      s.referrerPolicy = "no-referrer-when-downgrade";
      s.onload = onReady;
      s.onerror = () => {
        s.remove();
        if (attempt < 3) setTimeout(() => tryLoad(attempt + 1), 1200 * attempt);
        else resolve(false);
      };
      document.body.appendChild(s);
    };
    tryLoad(1);
  });
  return window.__smoobuLoading;
}

export default function Booking() {
  const { t, lang } = useI18n();
  const sectionRef = useRef(null);
  const hostRef = useRef(null);
  const initSeqRef = useRef(0);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [conditionsOpen, setConditionsOpen] = useState(false);

  const renderWidget = useCallback(async () => {
    const host = hostRef.current;
    if (!host) return;
    initSeqRef.current += 1;
    const mySeq = initSeqRef.current;
    setStatus("loading");
    host.innerHTML = '<div id="apartmentIframeAll"></div>';

    const ok = await loadSmoobuScript();
    if (mySeq !== initSeqRef.current) return;
    if (!ok || !window.BookingToolIframe) {
      setStatus("error");
      return;
    }
    const target = host.querySelector("#apartmentIframeAll");
    if (target) target.innerHTML = "";
    try {
      window.BookingToolIframe.initialize({
        url: `https://login.smoobu.com/${lang}/booking-tool/iframe/${SMOOBU_APT_ID}`,
        baseUrl: "https://login.smoobu.com",
        target: "#apartmentIframeAll",
      });

      // Confirm an iframe actually got injected; if not after a window, mark error.
      setTimeout(() => {
        if (mySeq !== initSeqRef.current) return;
        const iframe = host.querySelector("#apartmentIframeAll iframe");
        if (iframe) setStatus("ready");
        else setStatus("error");
      }, 1500);
    } catch (e) {
      setStatus("error");
    }
  }, [lang]);

  // Init when the booking section enters the viewport (gives Smoobu's CDN
  // time on slow connections) AND re-init when lang changes.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      renderWidget();
    };

    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              trigger();
              io.disconnect();
            }
          });
        },
        { rootMargin: "400px 0px" }
      );
      io.observe(node);
      // Safety fallback: if observer never fires (rare), init after 5s.
      const safety = setTimeout(trigger, 5000);
      return () => {
        clearTimeout(safety);
        io.disconnect();
      };
    } else {
      trigger();
    }
  }, [renderWidget]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      initSeqRef.current += 1;
      if (hostRef.current) hostRef.current.innerHTML = "";
    };
  }, []);

  // Conditions modal — body scroll lock
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    if (conditionsOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = prev || "";
    return () => { document.body.style.overflow = prev || ""; };
  }, [conditionsOpen]);

  const conditionsModal = (
    <AnimatePresence>
      {conditionsOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 flex items-center justify-center p-4 md:p-8"
          style={{ zIndex: 9998, backgroundColor: "rgba(31,31,29,0.55)" }}
          onClick={() => setConditionsOpen(false)}
          data-testid="conditions-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-ivory rounded-md shadow-[0_30px_80px_-30px_rgba(31,31,29,0.5)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="conditions-title"
          >
            <button
              type="button"
              onClick={() => setConditionsOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-charcoal/60 hover:text-charcoal hover:bg-sand-100 transition-all duration-300"
              aria-label={t.booking.conditions.close}
              data-testid="conditions-close"
            >
              <X size={16} strokeWidth={1.4} />
            </button>
            <div className="px-7 md:px-12 pt-10 md:pt-14 pb-10 md:pb-14">
              <span className="eyebrow">{t.booking.eyebrow}</span>
              <h3
                id="conditions-title"
                className="font-serif-display text-3xl md:text-4xl text-charcoal mt-3 leading-tight"
              >
                {t.booking.conditions.title}
              </h3>
              <div className="mt-8 space-y-7">
                {t.booking.conditions.sections.map((s, i) => (
                  <div key={i} data-testid={`conditions-section-${i}`}>
                    <h4 className="font-serif text-lg md:text-xl text-charcoal">{s.h}</h4>
                    <p className="mt-1.5 text-sm md:text-[0.95rem] text-charcoal-soft leading-relaxed whitespace-pre-line">
                      {s.p}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <section
      id="booking"
      ref={sectionRef}
      className="relative py-20 md:py-28 lg:py-32 bg-sand-100 overflow-hidden"
      data-testid="booking-section"
    >
      <div className="absolute inset-y-0 right-0 w-1/3 hidden lg:block opacity-90 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1771842712919-3e7687ccabd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxtZWRpdGVycmFuZWFuJTIwc2VhJTIwc3VucmlzZSUyMGNvYXN0bGluZXxlbnwwfHx8fDE3Nzg1MDIzMTR8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-sand-100/30" />
      </div>

      <div className="relative px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-12 gap-y-10 gap-x-8 lg:gap-x-14 items-start">
          <div className="col-span-12 lg:col-span-5">
            <span className="eyebrow">{t.booking.eyebrow}</span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-charcoal mt-4 whitespace-pre-line"
              data-testid="booking-title"
            >
              {t.booking.title}
            </motion.h2>
            <p className="mt-6 text-charcoal-soft text-base md:text-lg leading-relaxed max-w-md">
              {t.booking.sub}
            </p>
            <div className="mt-8 flex items-center gap-3 text-xs tracking-luxe uppercase text-charcoal" data-testid="booking-trust">
              <span className="block w-10 h-px bg-charcoal/40" />
              <span>{t.booking.trust}</span>
            </div>
            <button
              type="button"
              onClick={() => setConditionsOpen(true)}
              data-testid="conditions-open"
              className="mt-4 text-[0.7rem] tracking-wider uppercase text-charcoal-mute hover:text-charcoal link-elegant"
            >
              {t.booking.conditions_link}
            </button>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 1 }}
            className="col-span-12 lg:col-span-7"
          >
            <div
              className="relative bg-ivory border border-charcoal/10 shadow-[0_30px_80px_-30px_rgba(31,31,29,0.18)] p-4 md:p-6 lg:p-7"
              data-testid="booking-card"
            >
              <div
                ref={hostRef}
                data-testid="smoobu-host"
                className="smoobu-host min-h-[420px] md:min-h-[460px]"
              />
              {/* Loading skeleton */}
              {status === "loading" && (
                <div className="absolute inset-4 md:inset-6 lg:inset-7 flex items-center justify-center bg-ivory pointer-events-none">
                  <div className="flex items-center gap-3 text-[0.65rem] tracking-luxe uppercase text-charcoal-mute">
                    <span className="inline-block w-2 h-2 rounded-full bg-charcoal/40 animate-pulse" />
                    {lang === "it" ? "Caricamento" : "Loading"}
                  </div>
                </div>
              )}
              {status === "error" && (
                <div className="absolute inset-4 md:inset-6 lg:inset-7 flex flex-col items-center justify-center bg-ivory text-center px-6 gap-4">
                  <p className="text-charcoal text-sm">
                    {lang === "it"
                      ? "Il sistema di prenotazione non si è caricato correttamente."
                      : "The booking system did not load correctly."}
                  </p>
                  <button
                    type="button"
                    onClick={renderWidget}
                    data-testid="smoobu-retry"
                    className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-5 py-2.5 text-[0.65rem] tracking-luxe uppercase hover:bg-charcoal hover:text-ivory transition-all duration-500"
                  >
                    <RefreshCcw size={13} strokeWidth={1.4} />
                    {lang === "it" ? "Ricarica" : "Reload"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {typeof document !== "undefined" ? createPortal(conditionsModal, document.body) : conditionsModal}
    </section>
  );
}
